import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAuth } from '@/hooks/useAuth';
import { useHivePackCustomization, useHivePackTerminology, useHivePackFeatures } from '@/hooks/useHivePackCustomization';

const HivePackDemo: React.FC = () => {
  const { organizationId } = useAuth();
  const customization = useHivePackCustomization(organizationId);
  const terminology = useHivePackTerminology(organizationId);
  const { hasFeature } = useHivePackFeatures(organizationId);

  if (!customization.isCustomized) {
    return (
      <Card className="border-yellow-200 bg-yellow-50">
        <CardHeader>
          <CardTitle>🏷️ No HivePack Selected</CardTitle>
          <CardDescription>
            Select a HivePack in Settings to see specialized terminology and features for your industry.
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card className="border-green-200 bg-green-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            🎯 Active HivePack: {customization.organizationType}
            <Badge variant="default" className="bg-green-500">Active</Badge>
          </CardTitle>
          <CardDescription>
            Your app experience is now customized for {customization.organizationType} businesses.
          </CardDescription>
        </CardHeader>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>📝 Custom Terminology</CardTitle>
            <CardDescription>See how terminology changes for your industry</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Students:</span>
              <Badge variant="outline">{terminology.students}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Classes:</span>
              <Badge variant="outline">{terminology.classes}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Instructors:</span>
              <Badge variant="outline">{terminology.instructors}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Location:</span>
              <Badge variant="outline">{terminology.location}</Badge>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-sm text-muted-foreground">Room:</span>
              <Badge variant="outline">{terminology.room}</Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>⚡ Enabled Features</CardTitle>
            <CardDescription>Features active for your HivePack</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="grid grid-cols-2 gap-2">
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${hasFeature('recitals') ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Recitals</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${hasFeature('competitions') ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Competitions</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${hasFeature('camps') ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Camps</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${hasFeature('privateInstruction') ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Private Lessons</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${hasFeature('apparelShop') ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Apparel Shop</span>
              </div>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${hasFeature('makeupClasses') ? 'bg-green-500' : 'bg-gray-300'}`} />
                <span className="text-sm">Makeup Classes</span>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {customization.industrySettings && (
        <Card>
          <CardHeader>
            <CardTitle>🏢 Industry-Specific Settings</CardTitle>
            <CardDescription>Specialized configurations for your {customization.organizationType} business</CardDescription>
          </CardHeader>
          <CardContent>
            <pre className="text-sm bg-gray-100 p-4 rounded-lg overflow-auto">
              {JSON.stringify(customization.industrySettings, null, 2)}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default HivePackDemo;